import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  Link,
  Typography,
} from "@mui/material";
import ArrowBack from "@mui/icons-material/ArrowBack";
import currency from "currency.js";

import { getContractLegal } from "../../../services/backend/ContractLegalService";
import { CONTRACT_STATUS, PERMISSIONS } from "../../../constants";
import { getContractField } from "../../../functions";
import CircularProgress from "../../generic/CircularProgress";
import ContractLegalBilling from "./payments/ContractLegalBilling";

// Vista de detalle (solo lectura) del contrato de persona jurídica, para
// gestor y supervisor. Las novedades llegan con C6.
//
// Aquí NO hay plan de pagos: el contrato jurídico no compromete cuotas
// previstas, la empresa factura cuando emite factura. Lo que muestra la vista
// son las facturas presentadas y el estado financiero que resulta de ellas
// (ContractLegalBilling).

// Misma pastilla de estado que usan los formularios de contrato natural y
// jurídico, para no introducir un estilo distinto en la vista.
const StatusPill = ({ status }) => (
  <Box
    component="span"
    sx={{
      display: "inline-block",
      backgroundColor: "#e3eed9ff",
      borderRadius: 5,
      color: "gray",
      px: 1.5,
      py: 0.75,
      ml: 1,
      textAlign: "center",
      fontSize: "0.6rem",
      verticalAlign: "middle",
    }}
  >
    {status}
  </Box>
);

const Row = ({ label, value }) => (
  <Box sx={{ display: "flex", py: 0.5 }}>
    <Typography variant="body2" color="text.secondary" sx={{ width: 220, flexShrink: 0 }}>
      {label}
    </Typography>
    <Typography variant="body2">{value ?? "—"}</Typography>
  </Box>
);

const ContractLegalView = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.value);
  const { contractId } = useParams();
  const [contract, setContract] = useState(null);

  useEffect(() => {
    (async () => {
      const response = await getContractLegal(contractId, user);
      if (!response) return navigate("/contracts");
      setContract(response);
    })();
    // eslint-disable-next-line
  }, [contractId]);

  const getField = useCallback(
    (key) => (contract ? getContractField(contract.news, contract)(key) : null),
    [contract]
  );

  if (!contract) {
    return (
      <Box sx={{ textAlign: "center", mt: 6 }}>
        <CircularProgress size={36} />
      </Box>
    );
  }

  const canEdit = user.permissions.includes(PERMISSIONS.CONTRACT_LEGAL.UPDATE)
    || contract.supervisorIDs?.includes(user.userId)
    || user.roles.includes("ADMIN");

  const fmt = (v) => currency(v || 0, { precision: 0 }).format();

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h5">
          <Button variant="outlined" startIcon={<ArrowBack />} sx={{ mr: 2 }} onClick={() => navigate("/contracts")}>
            Volver
          </Button>
          {contract.contractCode}
          <Chip label="Persona Jurídica" size="small" color="info" sx={{ ml: 1 }} />
          <StatusPill status={getField("status") || contract.status} />
        </Typography>
        {canEdit && ![CONTRACT_STATUS.CLOSED].includes(contract.status) && (
          <Button variant="contained" onClick={() => navigate(`/contracts/legal/${contract.contractCode}`)}>
            Editar contrato
          </Button>
        )}
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>Empresa contratista</Typography>
              <Row label="Razón social" value={contract.entity?.legalName} />
              <Row label="NIT" value={contract.entity?.nit} />
              <Row label="Representante legal" value={contract.entity?.legalRepName} />
              <Row label="Identificación rep. legal" value={contract.entity?.legalRepIdNumber} />
              <Row label="Correo" value={contract.entity?.email} />
              <Row label="Teléfono" value={contract.entity?.phone} />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>Información del contrato</Typography>
              <Row label="Valor del contrato" value={fmt(getField("contractBudget"))} />
              <Row label="Fecha de inicio" value={contract.startedAt?.format?.("YYYY-MM-DD")} />
              <Row label="Fecha de cierre" value={getField("finishedAt")?.format?.("YYYY-MM-DD")} />
              <Row label="Duración (días)" value={getField("contractPeriod")} />
              <Row label="Dependencia" value={contract.dependency} />
              <Row label="Naturaleza" value={contract.nature} />
              <Row label="Modalidad" value={contract.modality} />
              <Row label="Tipo de contrato" value={contract.modalityContractType} />
              <Row label="Supervisores" value={(contract.supervisors || []).map((s) => `${s.name} ${s.lastName}`).join(", ")} />
              {contract.secopUrl && (
                <Row label="SECOP" value={<Link href={contract.secopUrl} target="_blank">Ver en SECOP</Link>} />
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>Objeto del contrato</Typography>
              <Typography variant="body2">{contract.contractSubject || "—"}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <ContractLegalBilling contract={contract} />
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>Obligaciones del contratista</Typography>
              {(contract.tasks || []).length === 0 ? (
                <Typography variant="body2" color="text.secondary">Sin obligaciones diligenciadas.</Typography>
              ) : (
                <ol style={{ margin: 0, paddingLeft: 20 }}>
                  {(getField("tasks") || contract.tasks).map((task, index) => (
                    <li key={index}>
                      <Typography variant="body2">{task.title || task}</Typography>
                    </li>
                  ))}
                </ol>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ContractLegalView;
